import { useCallback, useState } from "react";
import useSWR, { mutate } from "swr";
import {
  type UserSearchHistoryCommercialRegistrationReceptionBookResponseType,
  type UserSearchHistoryCommercialRegistrationReceptionBookListResponseType,
} from "../types";
import { useApiClient } from "@/hooks/useApiClient";
import { type AxiosError } from "axios";

// 商業登記用検索履歴APIエンドポイント
// TODO: 商業登記用の検索履歴APIが実装された際にエンドポイントを更新する
const COMMERCIAL_SEARCH_HISTORY_ENDPOINT =
  "/user-search-history/commercial-registration-reception-book";

export const useReceptionBookSearchHistory = (
  shouldFetch = false
): {
  searchHistoryData: UserSearchHistoryCommercialRegistrationReceptionBookResponseType[];
  totalCount: number;
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<
    UserSearchHistoryCommercialRegistrationReceptionBookResponseType[]
  >;
} => {
  const { apiClient } = useApiClient();
  const [isManualLoading, setIsManualLoading] = useState(false);

  const fetcher = useCallback(
    async (
      url: string
    ): Promise<UserSearchHistoryCommercialRegistrationReceptionBookListResponseType> => {
      const response =
        await apiClient.get<UserSearchHistoryCommercialRegistrationReceptionBookListResponseType>(
          url
        );
      return response.data;
    },
    [apiClient]
  );

  const { data, error } = useSWR<
    UserSearchHistoryCommercialRegistrationReceptionBookListResponseType,
    AxiosError
  >(shouldFetch ? COMMERCIAL_SEARCH_HISTORY_ENDPOINT : null, fetcher, {
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
    suspense: false,
    shouldRetryOnError: false,
  });

  // 必要なときに手動でデータを取得するための再取得関数
  const refetch = useCallback(async (): Promise<
    UserSearchHistoryCommercialRegistrationReceptionBookResponseType[]
  > => {
    try {
      setIsManualLoading(true);
      const result = await fetcher(COMMERCIAL_SEARCH_HISTORY_ENDPOINT);
      // 第3引数をfalseにして再検証を防止する
      void mutate(COMMERCIAL_SEARCH_HISTORY_ENDPOINT, result, false);
      return result.items;
    } catch (e) {
      console.error("検索履歴の取得に失敗しました:", e);
      void mutate(COMMERCIAL_SEARCH_HISTORY_ENDPOINT, undefined, false);
      throw e;
    } finally {
      setIsManualLoading(false);
    }
  }, [fetcher]);

  const isLoading = (shouldFetch && !error && !data) || isManualLoading;

  return {
    searchHistoryData: data?.items ?? [],
    totalCount: data?.count ?? 0,
    isLoading,
    isError: !!error,
    refetch,
  };
};
